import type { CognitoUser } from 'amazon-cognito-identity-js';

import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { Auth } from 'aws-amplify';

import { getAuthConfig } from '@/shared/config';

import { AMPLIFY_PROVIDER_STORAGE_KEY } from '../constants/amplify';
import { SSO_PROVIDERS, SSO_PROVIDERS_LIST } from '../constants/ssoProviders';

export const AUTH_API = {
  changePassword: async (oldPassword: string, newPassword: string): Promise<unknown> => {
    try {
      const user = await AUTH_API.checkUser();
      return (await Auth.changePassword(user, oldPassword, newPassword)) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  checkUser: async (): Promise<CognitoUser> => {
    try {
      return (await Auth.currentAuthenticatedUser()) as CognitoUser;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  confirmSignUp: async (username: string, code: string): Promise<unknown> => {
    try {
      return (await Auth.confirmSignUp(username, code)) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  forgotPassword: async (username: string): Promise<unknown> => {
    try {
      return (await Auth.forgotPassword(username)) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  forgotPasswordSubmit: async (
    username: string,
    code: string,
    newPassword: string,
  ): Promise<unknown> => {
    try {
      return (await Auth.forgotPasswordSubmit(username, code, newPassword)) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  resendConfirmationCode: async (username: string): Promise<unknown> => {
    try {
      return (await Auth.resendSignUp(username)) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  signIn: async (username: string, password: string): Promise<unknown> => {
    try {
      return (await Auth.signIn(username, password)) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  signOut: async (): Promise<unknown> => {
    try {
      localStorage.removeItem(AMPLIFY_PROVIDER_STORAGE_KEY);
      return (await Auth.signOut()) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  signUp: async (username: string, password: string, email: string): Promise<unknown> => {
    try {
      const response = (await Auth.signUp({
        attributes: {
          email,
        },
        autoSignIn: {
          // optional - enables auto sign in after user is confirmed
          enabled: true,
        },
        password,
        username: email,
      })) as { user: unknown };
      const { user } = response;
      return user;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  socialSignIn: async (
    socialNetwork: CognitoHostedUIIdentityProvider | SSO_PROVIDERS,
  ): Promise<unknown> => {
    try {
      const isSsoProvider = SSO_PROVIDERS_LIST.includes(socialNetwork);

      if (isSsoProvider) {
        localStorage.setItem(AMPLIFY_PROVIDER_STORAGE_KEY, socialNetwork);
        const config = getAuthConfig(socialNetwork);
        Auth.configure(config);
      }

      return (await Auth.federatedSignIn({
        provider: socialNetwork as CognitoHostedUIIdentityProvider,
      })) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  deleteAccount: async (): Promise<unknown> => {
    try {
      return (await Auth.deleteUser()) as unknown;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  getAccessToken: async (): Promise<string> => {
    try {
      const session = await Auth.currentSession();
      return session.getAccessToken().getJwtToken();
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getTokenId: async (): Promise<string> => {
    try {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    } catch (e) {
      return Promise.reject(e);
    }
  },
  getUserInfo: async (): Promise<unknown> => {
    try {
      return (await Auth.currentUserInfo()) as unknown;
    } catch (e) {
      return Promise.reject(e);
    }
  },
};
